import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import ScienceIcon from '@mui/icons-material/Science';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SearchIcon from '@mui/icons-material/Search';
import ListAddRequestDialog from './ListAddRequestDialog';

/**
 * Working List Dialog Component
 * Dialog for managing parameter lists (adding/removing parameters)
 *
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Handler for closing dialog
 * @param {number} selectedParameterId - Parameter ID to manage
 * @param {Object} selectedParameter - Complete parameter object
 * @param {Array} parameterLists - All parameter lists
 * @param {string} currentUser - Current user name
 * @param {Function} isParameterInList - Check if parameter is in list
 * @param {Function} handleToggleParameterInList - Toggle parameter in list
 * @param {Function} hasParameterPendingApproval - Check if parameter has pending approval
 * @param {Function} setSnackbar - Function to show snackbar feedback
 */
const WorkingListDialog = ({
  open,
  onClose,
  selectedParameterId,
  selectedParameter,
  parameterLists,
  currentUser,
  isParameterInList,
  handleToggleParameterInList,
  hasParameterPendingApproval,
  setSnackbar,
}) => {
  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    list: null
  });

  // Handle public list click - open confirmation dialog
  const handlePublicListClick = (list) => {
    setConfirmDialog({
      open: true,
      list: list
    });
  };

  // Handle list toggle with feedback
  const handleListToggleWithFeedback = (parameterId, listId) => {
    const result = handleToggleParameterInList(parameterId, listId);

    // Show feedback
    if (result.success) {
      setSnackbar?.({
        open: true,
        message: result.message,
        severity: 'success'
      });
    } else if (result.info) {
      setSnackbar?.({
        open: true,
        message: result.message,
        severity: 'info'
      });
    } else {
      setSnackbar?.({
        open: true,
        message: result.message || 'Ein Fehler ist aufgetreten',
        severity: 'error'
      });
    }
  };

  // Handle confirmation of public list addition
  const handleConfirmAddRequest = () => {
    if (confirmDialog.list) {
      handleListToggleWithFeedback(selectedParameterId, confirmDialog.list.id);
      setConfirmDialog({ open: false, list: null });
    }
  };

  // Close confirmation dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, list: null });
  };

  // Helper function to get list icon
  const getListIcon = (listName, isDefault) => {
    if (isDefault) return <StarIcon sx={{ fontSize: 24 }} />;
    if (listName.toLowerCase().includes('studie')) return <ScienceIcon sx={{ fontSize: 24 }} />;
    if (listName.toLowerCase().includes('icu') || listName.toLowerCase().includes('intensiv'))
      return <LocalHospitalIcon sx={{ fontSize: 24 }} />;
    return <BookmarkIcon sx={{ fontSize: 24 }} />;
  };

  // Filter lists based on search term
  const filterLists = (lists) => {
    if (!searchTerm.trim()) return lists;
    return lists.filter(list =>
      list.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      list.createdBy?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const allPrivateLists = parameterLists?.filter(list => list.isPrivate && list.createdBy === currentUser) || [];
  const allPublicLists = parameterLists?.filter(list => !list.isPrivate) || [];

  const privateLists = filterLists(allPrivateLists);
  const publicLists = filterLists(allPublicLists);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BookmarkIcon />
          <Typography variant="h6" fontWeight="bold">
            Listen verwalten
          </Typography>
        </Box>
        {selectedParameter && (
          <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'primary.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
            <Typography variant="body2" fontWeight="medium" color="primary.dark">
              Parameter: {selectedParameter.name}
            </Typography>
          </Box>
        )}
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Wählen Sie Listen aus, um den Parameter hinzuzufügen oder zu entfernen
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        {/* Search Field */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Listen durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
              }
            }}
          />
        </Box>

        {/* Private Lists Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <LockIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight="bold">
              Meine privaten Listen
            </Typography>
            <Chip
              label={searchTerm ? `${privateLists.length} von ${allPrivateLists.length}` : privateLists.length}
              size="small"
              sx={{ height: 20 }}
              color={searchTerm && privateLists.length < allPrivateLists.length ? "primary" : "default"}
            />
          </Box>

          {privateLists.length > 0 ? (
            <Grid container spacing={1.5}>
              {privateLists.map(list => {
                const isInList = isParameterInList(selectedParameterId, list.id);
                return (
                  <Grid item xs={12} sm={6} md={4} key={list.id}>
                    <Card
                      elevation={isInList ? 3 : 1}
                      sx={{
                        height: '100%',
                        border: isInList ? '2px solid' : '1px solid',
                        borderColor: isInList ? 'success.main' : 'divider',
                        transition: 'all 0.15s ease-in-out',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        }
                      }}
                      onClick={() => handleListToggleWithFeedback(selectedParameterId, list.id)}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {/* Icon */}
                          <Box sx={{
                            color: isInList ? 'success.main' : 'text.secondary',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {getListIcon(list.name, list.isDefault)}
                          </Box>

                          {/* Name & Count */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {list.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {list.parameterIds.length} Parameter
                            </Typography>
                          </Box>

                          {/* Status Icon */}
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isInList ? (
                              <CheckCircleIcon sx={{ fontSize: 24, color: 'success.main' }} />
                            ) : (
                              <AddIcon sx={{ fontSize: 24, color: 'action.active' }} />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}

              {/* New List Card - only show if not searching */}
              {!searchTerm && (
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      border: '2px dashed',
                      borderColor: 'divider',
                      backgroundColor: 'grey.50',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'primary.50',
                      }
                    }}
                  >
                    <CardActionArea disabled sx={{ height: '100%' }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <AddIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="bold" color="textSecondary">
                              Neue Liste erstellen
                            </Typography>
                            <Typography variant="caption" color="textDisabled">
                              Demnächst verfügbar
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              )}
            </Grid>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: 'grey.50'
              }}
            >
              <Typography variant="caption" color="textSecondary">
                {searchTerm ? 'Keine Listen gefunden' : 'Keine privaten Listen vorhanden'}
              </Typography>
            </Paper>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Public Lists Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <PublicIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight="bold">
              Öffentliche Listen
            </Typography>
            <Chip
              label={searchTerm ? `${publicLists.length} von ${allPublicLists.length}` : publicLists.length}
              size="small"
              sx={{ height: 20 }}
              color={searchTerm && publicLists.length < allPublicLists.length ? "primary" : "default"}
            />
          </Box>

          {publicLists.length > 0 ? (
            <Grid container spacing={1.5}>
              {publicLists.map(list => {
                const isInList = isParameterInList(selectedParameterId, list.id);
                const hasPending = hasParameterPendingApproval(selectedParameterId, list.id);

                return (
                  <Grid item xs={12} sm={6} md={4} key={list.id}>
                    <Card
                      elevation={isInList ? 3 : hasPending ? 2 : 1}
                      sx={{
                        height: '100%',
                        border: isInList ? '2px solid' : hasPending ? '2px solid' : '1px solid',
                        borderColor: isInList ? 'success.main' : hasPending ? 'warning.main' : 'divider',
                        transition: 'all 0.15s ease-in-out',
                        cursor: (isInList || hasPending) ? 'default' : 'pointer',
                        opacity: isInList ? 0.7 : 1,
                        backgroundColor: hasPending ? 'warning.50' : 'white',
                        '&:hover': {
                          transform: (isInList || hasPending) ? 'none' : 'translateY(-2px)',
                          boxShadow: (isInList || hasPending) ? 1 : 3,
                        },
                      }}
                      onClick={() => !isInList && !hasPending && handlePublicListClick(list)}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {/* Icon */}
                          <Box sx={{
                            color: isInList ? 'success.main' : hasPending ? 'warning.main' : 'text.secondary',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {getListIcon(list.name, false)}
                          </Box>

                          {/* Name, Count & Creator */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {list.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              <Typography variant="caption" color="textSecondary">
                                {list.parameterIds.length} Parameter
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                • {list.createdBy}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Status Icon */}
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isInList ? (
                              <CheckCircleIcon sx={{ fontSize: 24, color: 'success.main' }} />
                            ) : hasPending ? (
                              <WarningAmberIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                            ) : (
                              <AddIcon sx={{ fontSize: 24, color: 'action.active' }} />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: 'grey.50'
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Keine öffentlichen Listen verfügbar
              </Typography>
            </Paper>
          )}
        </Box>

        <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'info.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'info.main' }}>
          <Typography variant="caption" color="textSecondary">
            💡 Private Listen → sofort. Öffentliche Listen → Genehmigung nötig.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Schließen
        </Button>
      </DialogActions>

      {/* Confirmation Dialog for Public List Addition */}
      <ListAddRequestDialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        list={confirmDialog.list}
        parameterId={selectedParameterId}
        onConfirm={handleConfirmAddRequest}
      />
    </Dialog>
  );
};

export default WorkingListDialog;
